import React, { useCallback, useEffect, useState } from "react";
import {
  NotificationBoxItem,
  NotificationBoxItemTitle,
  NotificationBoxWrapper
} from "./notificationBoxWrapper";
import { useIntl } from "react-intl";
import { LocalizationMessages } from "../../../../localization/messages";
import { Actions, MyAccountTab } from "../../../../constants";
import { useAccountCommunicationHandlers } from "../../../../hooks";
import {
  NavigationUtils,
  PageContextUtils,
  TrackingUtils
} from "../../../../utils";
import { baseThemeData } from "../../../../styles/themes/baseTheme";
import { BrandUtils } from "../../../../utils/brandUtils";
import { useSelector } from "react-redux";
import {
  StateSwitcherSelectors,
  UserContextSelectors
} from "../../../../redux/selectors";
import { useTheme } from "styled-components";
import PropTypes from "prop-types";
import { EmptyComponent } from "../../../common/EmptyComponent";
import SVGIconV3 from "../../../common/SVGIcon/SVGIconV3";
import { Brand } from "../../../../configurations";
import { useLocationHandler } from "../../../../hooks/useLocationHandler";

const NotificationBox = props => {
  const { properties, trackingInfo, height, width } = props;
  const brandId = useSelector(UserContextSelectors.brandId);
  const isAuthenticated = useSelector(UserContextSelectors.isAuthenticated);
  const theme = useTheme();
  const isMobile = useSelector(UserContextSelectors.isMobile);
  const { openLocationConfirmationPopup } = useLocationHandler();
  const selectedState = useSelector(StateSwitcherSelectors.selectedState);

  const [componentList, setComponentList] = useState([]);
  const [color, setColor] = useState([]);

  const updateColor = (index, color) => {
    setColor(prevColor => {
      const updateColors = [...prevColor];
      updateColors[index] = color;
      return updateColors;
    });
  };
  useEffect(() => {
    if (properties && properties[0]?.propertyName === "componentsList") {
      const componentsList = properties[0]?.propertyValue?.split(",");
      setComponentList(componentsList);
      const initialColors = componentsList.map(() => theme.colors.textColor);
      setColor(initialColors);
    }
  }, [properties, theme.colors.textColor]);

  useEffect(() => {
    if (properties && properties[0]?.propertyName === "componentsList") {
      setComponentList(properties[0]?.propertyValue.split(","));
    }
  }, [properties]);

  const { formatMessage } = useIntl();
  const { openMyAccount, openChallengesOrPromotions } =
    useAccountCommunicationHandlers();
  const getItemConfig = item => {
    const { name } = PageContextUtils.getClientProviderData();

    const propsConfig = {
      Kambi: {
        CHALLENGES: {
          iconName: getChallengesIcon(),
          title: formatMessage(LocalizationMessages.challenges),
          handleClick: () => handleOpenChallenges(),
          dataTestId: "uc-my-account-area-challenges"
        },
        REWARDS: {
          iconName: "rewards",
          title: formatMessage(LocalizationMessages.rewards),
          handleClick: () =>
            isMobile
              ? mobileRewardsClick(openMyAccount, MyAccountTab.FreeBets)
              : handleOpenMyAccount(MyAccountTab.FreeBets, "Rewards"),
          dataTestId: "uc-my-account-area-rewards"
        }
      },
      Spectate: {
        MY_LOCKER: {
          iconName: "locker",
          title: formatMessage(LocalizationMessages.challenges),
          handleClick: () => handleOpenChallenges(),
          dataTestId: "uc-my-account-area-challenges"
        },
        CHALLENGES: {
          iconName: getChallengesIcon(),
          title: formatMessage(LocalizationMessages.challenges),
          handleClick: () => handleOpenChallenges(),
          dataTestId: "uc-my-account-area-challenges"
        },
        REWARDS: {
          iconName: "rewards",
          title: formatMessage(LocalizationMessages.rewards),
          handleClick: () =>
            isMobile
              ? mobileRewardsClick(openMyAccount, MyAccountTab.FreeBetsSpectate)
              : handleOpenMyAccount(getRewardsTabID(), "Rewards"),
          dataTestId: "uc-my-account-area-rewards"
        }
      },
      Poker: {
        TICKETS: {
          iconName: "ticket",
          title: formatMessage(LocalizationMessages.tickets_Poker),
          handleClick: () =>
            handleOpenMyAccount(MyAccountTab.Tickets, "Tickets"),
          dataTestId: "uc-my-account-area-tickets"
        },
        REWARDS: {
          iconName: "rewards",
          title: formatMessage(LocalizationMessages.rewards),
          handleClick: () =>
            handleOpenMyAccount(MyAccountTab.RewardsPWC, "Rewards"),
          dataTestId: "uc-my-account-area-rewards"
        },
        CHALLENGES: {
          iconName: "challanges-shield",
          title: formatMessage(LocalizationMessages.challenges),
          handleClick: () => handleOpenChallenges(),
          dataTestId: "uc-my-account-area-challenges"
        }
      }
    };
    return propsConfig[name][item] ?? null;
  };

  const mobileRewardsClick = useCallback(
    (openMyAccount, tabToOpen) => {
      TrackingUtils.sendAnalytics({
        ...trackingInfo,
        dataLabel: "Rewards"
      });

      openMyAccount({ tabId: tabToOpen });
    },
    [trackingInfo]
  );
  const handleOpenMyAccount = (tabToOpen, eventLabel) => {
    TrackingUtils.sendAnalytics({
      ...trackingInfo,
      dataLabel: eventLabel
    });

    if (isAuthenticated) {
      openMyAccount({ tabId: tabToOpen });
    } else if (
      BrandUtils.isSiSportsBook() &&
      (!selectedState || selectedState.length <= 1)
    ) {
      openLocationConfirmationPopup();
    } else {
      document.body.classList.add("no-scroll");
      const data = {
        actionID: Actions.openMyAccount,
        actionData: { tabId: tabToOpen }
      };
      NavigationUtils.runFlowRunner(data);
    }
  };

  const getRewardsTabID = useCallback(() => {
    switch (brandId) {
      case brandId === Brand.Sport_SE:
        return MyAccountTab.FreeBets;
      default:
        return MyAccountTab.FreeBetsSpectate;
    }
  }, [brandId]);

  const getChallengesIcon = () => {
    return BrandUtils.isPokerClient() ? "challanges-shield" : "challenges";
  };

  const handleOpenChallenges = () => {
    TrackingUtils.sendAnalytics({
      ...trackingInfo,
      dataLabel: "Challenges"
    });
    openChallengesOrPromotions();
  };

  if (!properties) return <EmptyComponent />;
  else
    return (
      <NotificationBoxWrapper>
        {componentList.map((item, index) => {
          const itemProps = getItemConfig(item);
          if (itemProps)
            return (
              <NotificationBoxItem
                key={index}
                onClick={itemProps.handleClick}
                applyHovverEffect={!isMobile}
                isAuthenticated={isAuthenticated}
                onMouseEnter={() => {
                  if (isMobile) {
                    updateColor(index, theme.colors.hooverLinkColors);
                  }
                }}
                onMouseLeave={() => {
                  if (isMobile) {
                    updateColor(index, theme.colors.textColor);
                  }
                }}
                data-testid={itemProps.dataTestId}
              >
                <SVGIconV3
                  iconName={itemProps.iconName}
                  className="notification-box-item-icon"
                  width={width}
                  height={height}
                  color={
                    isAuthenticated ? color[index] : baseThemeData.colorPrimary
                  }
                />
                <NotificationBoxItemTitle
                  color={
                    isAuthenticated ? color[index] : baseThemeData.colorPrimary
                  }
                >
                  {itemProps.title}
                </NotificationBoxItemTitle>
              </NotificationBoxItem>
            );
        })}
      </NotificationBoxWrapper>
    );
};

NotificationBox.propTypes = {
  properties: PropTypes.array,
  trackingInfo: PropTypes.object,
  height: PropTypes.string,
  width: PropTypes.string
};
export default React.memo(NotificationBox);
